import _ from 'lodash';

export interface Rating {
  label: string;
  value: number;
  range?: [number, number];
  likelihoodImpact?: {
    impact: number;
    likelihood: number;
  }[];
}

/**
 * Anything that declares a numeric value on a scale — a likelihood/impact
 * dimension option or a rating band. Structural so that i18n `Rating`,
 * aggregations `RatingCategory` and the web's rating option shapes all
 * satisfy it without mapping.
 */
export interface RatingScaleOption {
  value: number;
  range?: readonly number[];
}

/**
 * Anything that maps matrix cells to a rating — a taxonomy composite
 * (`risk_controlled`, many cells per option) or a matrix-config cell (one
 * cell per option). Same structural intent as `RatingScaleOption`.
 */
export interface RatingCellOption {
  likelihoodImpact?: readonly { likelihood: number; impact: number }[];
}

/**
 * Retrieve a rating option by range
 * @param options
 * @param value
 * @returns
 */
export const getRatingByRange = <
  T extends {
    range?: readonly number[];
  },
>(
  options: T[],
  value: number | null
): T | undefined => {
  if (_.isNil(value)) {
    return undefined;
  }

  return options.find((r) => {
    if (!r.range || r.range.length !== 2) {
      return undefined;
    }
    const [low, high] = r.range;
    if (low === undefined || high === undefined) {
      return undefined;
    }

    return value >= low && Math.floor(value) <= high;
  });
};

export const getRatingByLikelihoodAndImpact = <T extends RatingCellOption>(
  options: readonly T[],
  likelihood: number | null | undefined,
  impact: number | null | undefined
): T | undefined => {
  if (_.isNil(likelihood) || _.isNil(impact)) {
    return undefined;
  }

  return options.find((r) => {
    return r.likelihoodImpact?.some(
      (li) => li.likelihood === likelihood && li.impact === impact
    );
  });
};

// ─── Band rule (RSP-7282) ───────────────────────────────────────────
//
// A fractional score resolves to the rating whose declared value is the
// greatest declared value at or below the score; a score below the scale
// clamps to the lowest declared value. This is the single implementation
// shared by the server (packages/aggregations) and the browser
// (packages/web) — decision D10. Do not duplicate it into either.

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

// Every declared rating value is a positive integer (`risk-assessment-result-config`
// pins `value`, `likelihood` and `impact` to `.int().positive()`), so zero and
// negatives are never coordinates — they are the unassessed sentinel. Both
// `default-scoring` and `risk-aggregation-only` persist `?? 0` for a leaf with no
// assessment. Clamping those to the lowest declared value would silently rate every
// unassessed risk, in every organisation, whatever the rounding mode — so the band
// rule refuses them before clamping rather than each caller guarding separately
// (which is how the server and browser would drift apart, breaking D10 parity).
const isAssessedScore = (value: unknown): value is number =>
  isFiniteNumber(value) && value > 0;

// `[0, 0]` is the codebase's "no range declared" sentinel: every matrix-config
// cell carries it (rest-api `toRatingCategories`, web `toRatingOptions`), and
// `enterprise-risk-score` already refuses to treat it as a band.
const hasDegenerateRange = (option: RatingScaleOption): boolean =>
  option.range?.length === 2 && option.range[0] === 0 && option.range[1] === 0;

/**
 * The band rule on bare numbers: the greatest declared value at or below
 * `score`, or the lowest declared value when `score` is below all of them.
 * Order of `declaredValues` is irrelevant; non-finite entries are ignored.
 * Returns `undefined` only when nothing finite is declared or `score` is not
 * a finite number.
 */
export const bandToDeclaredValue = (
  declaredValues: readonly number[],
  score: number
): number | undefined => {
  if (!Number.isFinite(score)) {
    return undefined;
  }

  let lowest: number | undefined;
  let greatestAtOrBelow: number | undefined;
  for (const declared of declaredValues) {
    if (!Number.isFinite(declared)) {
      continue;
    }
    if (lowest === undefined || declared < lowest) {
      lowest = declared;
    }
    if (
      declared <= score &&
      (greatestAtOrBelow === undefined || declared > greatestAtOrBelow)
    ) {
      greatestAtOrBelow = declared;
    }
  }

  return greatestAtOrBelow ?? lowest;
};

/**
 * Band a single dimension score (a likelihood or an impact) to the option
 * declared at the greatest value at or below it — the browser's replacement
 * for exact `Map.get(value)` lookups against `likelihood` / `impact` option
 * sets. Options declaring the degenerate `[0, 0]` range are excluded from
 * consideration. Ties on `value` resolve to the first option, as
 * `getRatingByLikelihoodAndImpact` does.
 *
 * A non-positive score is the unassessed sentinel, not a coordinate below the
 * scale, so it resolves to nothing rather than clamping — see `isAssessedScore`.
 */
export const getRatingByBandedValue = <T extends RatingScaleOption>(
  options: readonly T[],
  score: number | null | undefined
): T | undefined => {
  if (!isAssessedScore(score)) {
    return undefined;
  }

  const candidates = options.filter((option) => !hasDegenerateRange(option));
  const banded = bandToDeclaredValue(
    candidates.map((option) => option.value),
    score
  );

  return candidates.find((option) => option.value === banded);
};

/**
 * The declared likelihood and impact coordinates implied by a set of cell
 * options: distinct, ascending, derived from `likelihoodImpact` alone. Under
 * the taxonomy source that is the 1–5 grid of `risk_controlled`; under the
 * matrix-config source it is whatever axes the org's matrix populates. This
 * is how both sources yield the same declared set from the same input shape.
 */
export const getDeclaredCoordinates = <T extends RatingCellOption>(
  options: readonly T[]
): { likelihoods: number[]; impacts: number[] } => {
  const likelihoods = new Set<number>();
  const impacts = new Set<number>();
  for (const option of options) {
    for (const cell of option.likelihoodImpact ?? []) {
      likelihoods.add(cell.likelihood);
      impacts.add(cell.impact);
    }
  }
  const ascending = (a: number, b: number) => a - b;

  return {
    likelihoods: [...likelihoods].sort(ascending),
    impacts: [...impacts].sort(ascending),
  };
};

/**
 * Outcome of the two-stage overall-rating resolution. Callers must be able
 * to tell a gapped-matrix miss from "nothing to resolve", so a miss is never
 * a bare `undefined`:
 *
 * - `missing_input` — likelihood or impact is null, undefined, non-finite or
 *   non-positive (the `?? 0` unassessed sentinel). Nothing to report; the risk
 *   has no assessment to rate.
 * - `no_declared_coordinates` — no option declares a cell, so there is no
 *   scale to band against. A configuration problem worth reporting.
 * - `no_cell_at_banded_coordinate` — banding landed on a declared coordinate
 *   but the matrix has no cell there: the documented gapped-matrix limitation
 *   (decision D11 = Option B). Report it; do not store an empty rating
 *   silently.
 */
export type BandedRatingResult<T> =
  | {
      status: 'resolved';
      rating: T;
      bandedLikelihood: number;
      bandedImpact: number;
    }
  | { status: 'unresolved'; reason: 'missing_input' }
  | { status: 'unresolved'; reason: 'no_declared_coordinates' }
  | {
      status: 'unresolved';
      reason: 'no_cell_at_banded_coordinate';
      bandedLikelihood: number;
      bandedImpact: number;
    };

/**
 * Resolve the overall rating for a (possibly fractional) likelihood/impact
 * pair in two stages: band each coordinate independently to a declared
 * value, then look the banded pair up as a cell via
 * `getRatingByLikelihoodAndImpact`. Whole-number inputs band to themselves,
 * so with rounding on this resolves exactly what the cell lookup resolves
 * today.
 */
export const getRatingByBandedLikelihoodAndImpact = <
  T extends RatingCellOption,
>(
  options: readonly T[],
  likelihood: number | null | undefined,
  impact: number | null | undefined
): BandedRatingResult<T> => {
  if (!isAssessedScore(likelihood) || !isAssessedScore(impact)) {
    return { status: 'unresolved', reason: 'missing_input' };
  }

  const declared = getDeclaredCoordinates(options);
  const bandedLikelihood = bandToDeclaredValue(
    declared.likelihoods,
    likelihood
  );
  const bandedImpact = bandToDeclaredValue(declared.impacts, impact);
  if (bandedLikelihood === undefined || bandedImpact === undefined) {
    return { status: 'unresolved', reason: 'no_declared_coordinates' };
  }

  const rating = getRatingByLikelihoodAndImpact(
    options,
    bandedLikelihood,
    bandedImpact
  );
  if (!rating) {
    return {
      status: 'unresolved',
      reason: 'no_cell_at_banded_coordinate',
      bandedLikelihood,
      bandedImpact,
    };
  }

  return { status: 'resolved', rating, bandedLikelihood, bandedImpact };
};
